import { useState, useCallback } from 'react';

export type SortDirection = 'asc' | 'desc';

interface UseSortingResult {
  sortColumn: string | null;
  sortDirection: SortDirection;
  handleSort: (column: string) => void;
  resetSort: () => void;
}

/**
 * Custom hook for managing table sorting with three states: asc -> desc -> null
 */
export const useSorting = (initialColumn: string | null = null, initialDirection: SortDirection = 'asc'): UseSortingResult => {
  const [sortColumn, setSortColumn] = useState<string | null>(initialColumn);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialDirection);

  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      // Same column: cycle through asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        // Reset to original (no sorting)
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      // New column: start with asc
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  const resetSort = useCallback(() => {
    setSortColumn(null);
    setSortDirection('asc');
  }, []);

  return {
    sortColumn,
    sortDirection,
    handleSort,
    resetSort,
  };
};
